import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {
    DetailEventTagDTO,
    EngineTagEventPersonalService,
    EngineTagEventService,
    IPersonalStatusLite,
    ITimbreQte,
    UpdateTagByDataviewDTO
} from '@tj/tj-dmp-lib-api';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {Paginator} from '../../../components/paginator';
import {ConfirmationService, LazyLoadEvent, MessageService, TreeNode} from 'primeng/api';
import {cronLabel} from '../../../components/input-cron/cron-label';
import {Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {tagJobPercent, TagJobService, TagJobStatus} from '../../../services/tag-job.service';

/**
 * Fiche d'un évènement de tag : une règle (preset, programmation, applications), une règle supprimée avec
 * son preset (historique seul), ou un tag appliqué une fois par un utilisateur.
 */
@Component({
    selector: 'dmp-tags-event-detail',
    templateUrl: './tags-event-detail.component.html',
    styleUrls: ['./tags-event-detail.component.scss'],
    standalone: false
})
export class TagsEventDetailComponent implements OnInit, OnDestroy {

    breadcrumb = [];
    description = '';

    datastore: string = null;
    id: number = null;
    timbre: Date = null;
    detail: DetailEventTagDTO = null;
    filters: TreeNode[] = [];
    personals: IPersonalStatusLite[];
    selectedPersonals: IPersonalStatusLite[] = [];
    timbres: Array<ITimbreQte>;
    timbrePaginator = new Paginator();
    personalPaginator = new Paginator();

    inprogress = false;
    /** Application de la règle en cours, en tâche de fond de l'engine : avancement de la modale d'attente. */
    runJob: TagJobStatus = null;
    private runSub: Subscription = null;

    /**
     * Échec de la dernière action, affiché sur la page près des actions. Le toast de l'intercepteur ne
     * suffit pas : il ne part que si le serveur renvoie un titre (un 404 passait en silence).
     */
    actionError: string = null;
    /** Application dont les contacts sont affichés (sélection du tableau de gauche). */
    selectedRun: ITimbreQte = null;

    /** Programmation : lue en texte, modifiée à la demande (« Modifier »). */
    editingSchedule = false;
    /** Programmation en cours de saisie (règle), comparée à celle enregistrée. */
    scheduled = false;
    cron: string = null;
    scheduleOptions: { label: string; value: boolean }[] = [];
    savingSchedule = false;

    constructor(protected readonly route: ActivatedRoute,
                protected readonly router: Router,
                protected readonly translate: TranslateService,
                protected readonly engineTagEventPersonalService: EngineTagEventPersonalService,
                protected readonly engineTagEventService: EngineTagEventService,
                protected readonly confirmationService: ConfirmationService,
                private readonly messageService: MessageService,
                private readonly tagJobService: TagJobService,
    ) {
        this.translate.onLangChange.subscribe(() => this.updateHeading());
    }

    get isRule(): boolean {
        return this.detail?.tagEvent.origin === 'REGLE';
    }

    get isDeletedRule(): boolean {
        return this.detail?.tagEvent.origin === 'REGLE_SUPPRIMEE';
    }

    get isUserEvent(): boolean {
        return this.detail?.tagEvent.origin === 'USER';
    }

    /** Un preset sans filtre, ou un ancien évènement enregistré sans eux (`{}`). */
    get hasFilters(): boolean {
        return this.filters[0]?.children?.length > 0;
    }

    get savedScheduleLabel(): string {
        const tagEvent = this.detail?.tagEvent;
        return tagEvent?.enableSchedule && tagEvent.cron
            ? cronLabel(tagEvent.cron)
            : this.translate.instant('tagsEventDetail.schedule.onDemand');
    }

    get scheduleChanged(): boolean {
        const tagEvent = this.detail?.tagEvent;
        return !!tagEvent && (this.scheduled !== !!tagEvent.enableSchedule || (this.scheduled && this.cron !== tagEvent.cron));
    }

    get canSaveSchedule(): boolean {
        return this.scheduleChanged && !this.savingSchedule && (!this.scheduled || !!this.cron);
    }

    async ngOnInit(): Promise<void> {
        this.datastore = this.route.snapshot.paramMap.get('tenant');
        this.id = Number(this.route.snapshot.paramMap.get('id'));
        if (this.route.snapshot.paramMap.has('iteration')) {
            this.timbre = moment(this.route.snapshot.paramMap.get('iteration')).toDate();
        }
        this.scheduleOptions = [
            {label: this.translate.instant('tagsEventDetail.schedule.onDemand'), value: false},
            {label: this.translate.instant('tagsEventDetail.schedule.scheduled'), value: true}
        ];
        this.updateDetail(() => {
            this.updateAffectation();
            this.updateTimbre();
        });
    }

    public updateTimbre(event?: LazyLoadEvent) {
        // Pagination des applications (c'était celle des contacts qui recevait la taille de page).
        this.timbrePaginator.rows = event ? event.rows : 10;
        this.timbrePaginator.first = event ? event.first / this.timbrePaginator.rows : 0;
        this.timbrePaginator.loading = true;
        this.engineTagEventPersonalService.getTimbreForTagEvent(this.datastore, this.id, this.timbrePaginator.first, this.timbrePaginator.rows, null, 'response').subscribe(res => {
            this.timbres = res.body;
            this.timbrePaginator.loading = false;
            this.timbrePaginator.totalRecords = +res.headers.get('x-total-count');
            // La plus récente (liste triée par date décroissante) : la page ne s'ouvre pas sur un vide.
            if (!this.timbre && this.timbres?.length > 0) {
                this.selectDate(this.timbres[0]);
            } else {
                this.selectedRun = this.timbres?.find(t => this.isSelectedTimbre(t)) ?? this.selectedRun;
            }
        });
    }

    public updateAffectation(event?: LazyLoadEvent) {
        if (this.timbre) {
            this.personalPaginator.rows = event ? event.rows : 10;
            this.personalPaginator.first = event ? event.first / this.personalPaginator.rows : 0;
            this.personalPaginator.loading = true;
            this.engineTagEventPersonalService.findPersonalForEvent(this.datastore, this.id, this.timbre, this.personalPaginator.first, this.personalPaginator.rows, null, 'response').subscribe(res => {
                this.personals = res.body;
                this.personalPaginator.loading = false;
                this.personalPaginator.totalRecords = +res.headers.get('x-total-count');
                this.selectedPersonals = [];
            });
        } else {
            this.personals = [];
        }
    }

    selectDate(data: ITimbreQte) {
        this.selectedRun = data;
        this.timbre = moment(data.timbre).toDate();
        this.updateAffectation();
    }

    /** Un second clic sur l'application affichée la garde sélectionnée. */
    keepRunSelected(data: ITimbreQte) {
        this.selectedRun = data;
    }

    isSelectedTimbre(data: ITimbreQte): boolean {
        return !!this.timbre && moment(data.timbre).isSame(this.timbre);
    }

    /**
     * Lancer la règle, c'est un tag en masse : le nombre de contacts concernés est annoncé avant
     * (docs/ui-design-guidelines.md §15.2), et le résultat réel après.
     */
    run() {
        this.actionError = null;
        const tagEvent = this.detail.tagEvent;
        const preset = this.detail.saveQuery?.name ?? '';
        const ask = (count: number | null) => this.confirmationService.confirm({
            header: this.translate.instant('tagsEventDetail.run.confirmHeader'),
            message: count == null
                ? this.translate.instant('tagsEventDetail.run.confirmNoCount', {preset})
                : this.translate.instant(`tagsEventDetail.run.confirm.${count === 1 ? 'one' : 'other'}`,
                    {preset, count: count.toLocaleString('fr-FR')}),
            acceptLabel: this.translate.instant('tagsEventDetail.run.accept'),
            rejectLabel: this.translate.instant('common.cancel'),
            accept: () => this.doRun()
        });
        this.engineTagEventPersonalService.countPersonalsForTagByDataview(
            {idDataview: tagEvent.dataview, queryFilter: this.detail.saveQuery?.query} as UpdateTagByDataviewDTO,
            this.datastore, tagEvent.partner ?? null
        ).subscribe({
            next: count => ask(count),
            error: () => ask(null)
        });
    }

    ngOnDestroy(): void {
        // La règle continue de s'appliquer côté engine ; seul son suivi s'arrête avec la page.
        this.runSub?.unsubscribe();
    }

    get runPercent(): number | null {
        return tagJobPercent(this.runJob);
    }

    private doRun() {
        const tagEvent = this.detail.tagEvent;
        const datastore = this.datastore;
        this.inprogress = true;
        this.runJob = null;
        // La règle s'applique en tâche de fond de l'engine, suivie jusqu'à sa fin : sur ~100 000 contacts,
        // elle dure trop pour tenir dans une requête. Sans affilié sur la règle, l'engine prend celui du
        // tenant (l'appel levait une erreur et laissait la modale « Traitement en cours » bloquée à l'écran).
        this.runSub = this.tagJobService.startRun(datastore, tagEvent.partner ?? null, tagEvent.id).pipe(
            switchMap(started => this.tagJobService.watch(datastore, started))
        ).subscribe({
            next: job => {
                this.runJob = job;
                if (job.status === 'COMPLETED') {
                    this.onRunDone(job.changes);
                } else if (job.status === 'FAILED') {
                    this.inprogress = false;
                    this.actionError = this.translate.instant('tagsEventDetail.errors.runInterrupted',
                        {count: job.changes.toLocaleString('fr-FR')});
                    // Ce qui a été appliqué avant l'arrêt l'est pour de bon.
                    this.timbre = null;
                    this.updateTimbre();
                }
            },
            error: err => {
                this.inprogress = false;
                // Tâche lancée puis suivi perdu (engine redémarré, réseau coupé) : elle a pu aboutir.
                if (this.runJob) {
                    this.actionError = this.translate.instant('tagsEventDetail.errors.runLost');
                } else {
                    this.showError('run', err);
                }
            }
        });
    }

    private onRunDone(changes: number) {
        this.inprogress = false;
        this.messageService.add({
            key: 'common',
            severity: changes > 0 ? 'success' : 'info',
            summary: this.translate.instant('tagsEventDetail.run.doneSummary'),
            detail: changes > 0
                ? this.translate.instant(`tagsEventDetail.run.done.${changes === 1 ? 'one' : 'other'}`,
                    {count: changes.toLocaleString('fr-FR')})
                : this.translate.instant('tagsEventDetail.run.noChange')
        });
        // Les contacts de la nouvelle application, en tête de liste.
        this.timbre = null;
        this.updateTimbre();
    }

    onScheduledChange() {
        // Valeur de départ : tous les jours à l'heure actuelle, arrondie aux 5 minutes inférieures.
        if (this.scheduled && !this.cron) {
            const now = new Date();
            this.cron = `${Math.floor(now.getMinutes() / 5) * 5} ${now.getHours()} * * *`;
        }
    }

    editSchedule() {
        this.actionError = null;
        this.resetSchedule();
        this.editingSchedule = true;
    }

    cancelSchedule() {
        this.resetSchedule();
        this.editingSchedule = false;
    }

    saveSchedule() {
        if (!this.canSaveSchedule) {
            return;
        }
        this.actionError = null;
        this.savingSchedule = true;
        this.engineTagEventService.updateTagEventSchedule(
            {enableSchedule: this.scheduled, cron: this.scheduled ? this.cron : null},
            this.datastore, this.detail.tagEvent.id
        ).subscribe({
            next: tagEvent => {
                this.savingSchedule = false;
                this.detail.tagEvent.enableSchedule = tagEvent.enableSchedule;
                this.detail.tagEvent.cron = tagEvent.cron;
                this.resetSchedule();
                this.editingSchedule = false;
                this.messageService.add({
                    key: 'common',
                    severity: 'success',
                    summary: this.translate.instant('tagsEventDetail.schedule.savedSummary'),
                    detail: this.savedScheduleLabel
                });
            },
            error: err => {
                this.savingSchedule = false;
                this.showError('schedule', err);
            }
        });
    }

    deleteAffectationPersonal() {
        const count = this.selectedPersonals.length;
        if (count > 0) {
            this.confirmationService.confirm({
                header: this.translate.instant('tagsEventDetail.history.undoHeader'),
                message: this.translate.instant(`tagsEventDetail.history.undoConfirm.${count === 1 ? 'one' : 'other'}`, {count}),
                acceptLabel: this.translate.instant('tagsEventDetail.history.undo'),
                rejectLabel: this.translate.instant('common.cancel'),
                accept: () => {
                    this.actionError = null;
                    const ids = this.selectedPersonals.map(p => p.tepId);
                    this.engineTagEventPersonalService.deleteTagEventPersonal(this.datastore, ids).subscribe({
                        next: () => {
                            this.updateAffectation();
                            this.updateTimbre();
                        },
                        error: err => this.showError('undo', err)
                    });
                }
            });
        }
    }

    /** Une règle est détachée, pas effacée : son historique porte les tags posés. */
    deleteRule() {
        this.confirmationService.confirm({
            header: this.translate.instant('tagsEventDetail.deleteRule.header'),
            message: this.translate.instant('tagsEventDetail.deleteRule.confirm'),
            acceptLabel: this.translate.instant('tagsEventDetail.deleteRule.accept'),
            rejectLabel: this.translate.instant('common.cancel'),
            accept: () => {
                this.actionError = null;
                this.engineTagEventService.detachRegle(this.datastore, this.detail.tagEvent.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            key: 'common',
                            severity: 'success',
                            summary: this.translate.instant('tagsEventDetail.deleteRule.doneSummary')
                        });
                        this.router.navigate(['/h/tag/event']);
                    },
                    error: err => this.showError('deleteRule', err)
                });
            }
        });
    }

    /** Évènement ponctuel sans aucune application : rien ne le référence, il peut être effacé. */
    deleteTagEvent() {
        this.confirmationService.confirm({
            header: this.translate.instant('tagsEventDetail.deleteEvent.header'),
            message: this.translate.instant('tagsEventDetail.deleteEvent.confirm'),
            acceptLabel: this.translate.instant('tagsEventDetail.deleteEvent.accept'),
            rejectLabel: this.translate.instant('common.cancel'),
            accept: () => {
                this.actionError = null;
                this.engineTagEventService.deleteTagEvent(this.datastore, this.detail.tagEvent.id).subscribe({
                    next: () => this.router.navigate(['/h/tag/event']),
                    error: err => this.showError('deleteEvent', err)
                });
            }
        });
    }

    /** « La règle n'a pas pu être supprimée : <raison>. » */
    private showError(action: string, err: unknown) {
        this.actionError = this.translate.instant(`tagsEventDetail.errors.${action}`, {reason: this.errorReason(err)});
    }

    private errorReason(err: unknown): string {
        // Déjà traduit par ErrorsInterceptor (réponse avec un titre) : « <message>; Message: … ».
        if (typeof err === 'string') {
            return err.split('; Message:')[0];
        }
        if (err instanceof HttpErrorResponse) {
            if (err.status === 0) {
                return this.translate.instant('tagsEventDetail.errors.reason.unreachable');
            }
            if (err.status === 403) {
                return this.translate.instant('tagsEventDetail.errors.reason.forbidden');
            }
            if (err.status === 404) {
                return this.translate.instant('tagsEventDetail.errors.reason.notFound');
            }
            if (err.error?.title) {
                return err.error.title;
            }
            return this.translate.instant('tagsEventDetail.errors.reason.server', {status: err.status});
        }
        return this.translate.instant('tagsEventDetail.errors.reason.unknown');
    }

    private resetSchedule() {
        this.scheduled = !!this.detail?.tagEvent.enableSchedule;
        this.cron = this.detail?.tagEvent.cron ?? null;
    }

    private updateDetail(callback?: () => void) {
        this.engineTagEventPersonalService.getHistoryDetail(this.datastore, this.id).subscribe(res => {
            this.detail = res;
            if (this.detail.saveQuery) {
                this.filters = [JSON.parse(this.detail.saveQuery.objectQueryJson)];
            } else if (this.detail.tagEvent.queryFilter) {
                this.filters = [JSON.parse(this.detail.tagEvent.queryFilter)];
            }
            this.resetSchedule();
            this.updateHeading();
            if (callback) {
                callback();
            }
        });
    }

    private updateHeading() {
        const kind = this.isRule ? 'rule' : this.isDeletedRule ? 'deletedRule' : 'event';
        this.breadcrumb = [
            {label: this.translate.instant('menu.dataviews.label')},
            {label: this.translate.instant('menu.dataviews.tag-events'), routerLink: '/h/tag/event'},
            {label: this.translate.instant(`tagsEventDetail.breadcrumb.${kind}`)}
        ];
        this.description = this.translate.instant(`tagsEventDetail.description.${kind}`);
    }
}
